import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { motion, useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { Product } from "../../appData/productData"
import { formatCurrency } from "../../utils/utils"
import styles from "./TidyPaywall.module.scss"

/**
 * TidyPaywall (/tidy) — an original, self-contained paywall.
 *
 * Divergence brief (see claude-paywalls/_ledger.md). Prior paywalls converged on
 * PRIVACY pitched as a DARK + SKEUOMORPHIC, centered-hero + one-glowing-card
 * screen. Every axis moves here:
 *   - Value frame:   ORGANIZATION / DECLUTTER (+ duplicate cleanup). The pitch
 *                    is "your chaotic camera roll, auto-sorted into albums" —
 *                    untouched by every prior paywall (all pitched privacy).
 *   - Layout:        a spatial HEAP→GRID canvas driven by a SCRUBBABLE TIMELINE.
 *                    No centered hero, no glowing card, no 4-icon perk grid.
 *   - Interaction:   SCRUBBABLE — drag a clay knob and watch the pile of photos
 *                    fly into labeled album trays and duplicates get binned.
 *   - Register:      SATISFYING / order-from-chaos (calm + a little delightful).
 *   - Art direction: CLAYMORPHIC, LIGHT, PHOTOGRAPHIC. Soft squishy surfaces,
 *                    real Unsplash photos. Not dark, not metal, no particles.
 *
 * Yearly is the prominent price (big tile); monthly is a small secondary pill.
 */

// ── Albums (real photos pre-fetched into public/tidy/) ──────────────────────
// Each album has 3 thumbnails; six albums fill a 3×2 tray grid when sorted.
interface AlbumDef {
  key: string
  label: string
  emoji: string
  files: string[]
  // tray centre as % of the canvas
  cx: number
  cy: number
}

const ALBUMS: AlbumDef[] = [
  {
    key: "mountains",
    label: "Trips",
    emoji: "🏔",
    files: ["mountains-1.jpg", "mountains-2.jpg", "mountains-3.jpg"],
    cx: 17,
    cy: 26,
  },
  {
    key: "beach",
    label: "Summer",
    emoji: "🏝",
    files: ["beach-1.jpg", "beach-2.jpg", "beach-3.jpg"],
    cx: 50,
    cy: 26,
  },
  {
    key: "pup",
    label: "The dog",
    emoji: "🐶",
    files: ["pup-1.jpg", "pup-2.jpg", "pup-3.jpg"],
    cx: 83,
    cy: 26,
  },
  {
    key: "food",
    label: "Food",
    emoji: "🍝",
    files: ["food-1.jpg", "food-2.jpg", "food-3.jpg"],
    cx: 17,
    cy: 66,
  },
  {
    key: "city",
    label: "Nights out",
    emoji: "🌃",
    files: ["city-1.jpg", "city-2.jpg", "city-3.jpg"],
    cx: 50,
    cy: 66,
  },
  {
    key: "blooms",
    label: "Garden",
    emoji: "🌷",
    files: ["blooms-1.jpg", "blooms-2.jpg", "blooms-3.jpg"],
    cx: 83,
    cy: 66,
  },
]

const PUB = process.env.PUBLIC_URL ?? ""
const src = (file: string) => `${PUB}/tidy/${file}`

// Fixed "messy" scatter for the 18 thumbnails — deterministic (no Math.random
// in the render path) so the pile looks the same every mount. x/y are % of the
// canvas; rot in degrees.
const SCATTER: { x: number; y: number; rot: number }[] = [
  { x: 30, y: 38, rot: -14 },
  { x: 58, y: 30, rot: 11 },
  { x: 44, y: 52, rot: -5 },
  { x: 67, y: 47, rot: 17 },
  { x: 38, y: 60, rot: 8 },
  { x: 52, y: 41, rot: -19 },
  { x: 61, y: 62, rot: 4 },
  { x: 35, y: 47, rot: 22 },
  { x: 49, y: 64, rot: -10 },
  { x: 64, y: 36, rot: 14 },
  { x: 41, y: 33, rot: -8 },
  { x: 55, y: 56, rot: 19 },
  { x: 33, y: 55, rot: 6 },
  { x: 70, y: 55, rot: -16 },
  { x: 46, y: 44, rot: 12 },
  { x: 59, y: 49, rot: -3 },
  { x: 39, y: 50, rot: 16 },
  { x: 63, y: 42, rot: -12 },
]

// In-tray fan offsets (px) and rotations for the 3 stacked photos.
const FAN = [
  { dx: -8, dy: -7, rot: -9 },
  { dx: 0, dy: 0, rot: 3 },
  { dx: 8, dy: 8, rot: 8 },
]

// Two "duplicate" thumbnails that get binned as you tidy (reused photos).
const DUPES = [
  { file: "pup-2.jpg", x: 45, y: 47, rot: -7 },
  { file: "food-1.jpg", x: 56, y: 53, rot: 9 },
]

const lerp = (a: number, b: number, t: number) => a + (b - a) * t
const clamp01 = (n: number) => (n < 0 ? 0 : n > 1 ? 1 : n)
const easeOut = (t: number) => 1 - Math.pow(1 - t, 3)

// Headline metrics (marketing figures — debug numPhotos is 0).
const TOTAL_PHOTOS = 4217
const TOTAL_DUPES = 390

const TidyPaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyNum = yearlyProduct?.price ?? 35.99
  const monthlyNum = monthlyProduct?.price ?? 13.99

  const yearlyPrice = formatCurrency(yearlyNum, locale)
  const yearlyPerMonth = formatCurrency(yearlyNum / 12, locale)
  const monthlyPrice = formatCurrency(monthlyNum, locale)
  const savePct = Math.max(
    0,
    Math.round((1 - yearlyNum / (monthlyNum * 12)) * 100),
  )

  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")
  const selected = plan === "yearly" ? yearlyProduct : monthlyProduct
  const hasTrial = selected?.hasTrial ?? plan === "yearly"

  // ── Scrub state: progress 0 (messy) → 1 (tidy) ────────────────────────────
  const [progress, setProgress] = useState(0)
  const trackRef = useRef<HTMLDivElement>(null)
  const draggingRef = useRef(false)
  const userTouchedRef = useRef(false)
  const rafRef = useRef<number>(0)

  // Auto-play the tidy once on mount so the value lands without input.
  useEffect(() => {
    if (reduceMotion) {
      setProgress(1)
      return
    }
    let start = 0
    const dur = 2300
    const tick = (t: number) => {
      if (userTouchedRef.current) return
      if (!start) start = t
      const p = clamp01((t - start) / dur)
      setProgress(easeOut(p))
      if (p < 1 && !userTouchedRef.current)
        rafRef.current = requestAnimationFrame(tick)
    }
    // brief beat of chaos before it tidies itself
    const id = setTimeout(() => {
      rafRef.current = requestAnimationFrame(tick)
    }, 550)
    return () => {
      clearTimeout(id)
      cancelAnimationFrame(rafRef.current)
    }
  }, [reduceMotion])

  const setFromClientX = useCallback((clientX: number) => {
    const el = trackRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    setProgress(clamp01((clientX - r.left) / r.width))
  }, [])

  const onPointerDown = (e: React.PointerEvent) => {
    userTouchedRef.current = true
    cancelAnimationFrame(rafRef.current)
    draggingRef.current = true
    ;(e.target as Element).setPointerCapture?.(e.pointerId)
    setFromClientX(e.clientX)
  }
  const onPointerMove = (e: React.PointerEvent) => {
    if (!draggingRef.current) return
    setFromClientX(e.clientX)
  }
  const onPointerUp = () => {
    draggingRef.current = false
  }

  // ── Derived live numbers ──────────────────────────────────────────────────
  const photosTidied = Math.round(progress * TOTAL_PHOTOS)
  const dupesGone = Math.round(clamp01((progress - 0.25) / 0.6) * TOTAL_DUPES)
  const albumsMade = Math.min(
    ALBUMS.length,
    Math.floor(clamp01((progress - 0.1) / 0.8) * (ALBUMS.length + 0.5)),
  )

  const statusText =
    progress < 0.04
      ? "12,000 photos. Zero albums."
      : progress > 0.985
        ? "All tidy ✨"
        : "Sorting…"

  // Per-photo eased local progress (a gentle cascade across the scrub).
  const localP = (i: number) => {
    const startAt = (i / 18) * 0.45
    return easeOut(clamp01((progress - startAt) / 0.55))
  }

  const handleClose = () => native.postNativeEvent("close")
  const handlePurchase = () => {
    if (selected) native.subscribe(selected as Product)
  }

  // Flatten albums → 18 photo descriptors with messy + sorted targets.
  const photos = useMemo(() => {
    const out: {
      file: string
      alb: AlbumDef
      fan: (typeof FAN)[number]
      scatter: (typeof SCATTER)[number]
      i: number
    }[] = []
    let i = 0
    ALBUMS.forEach((alb) => {
      alb.files.forEach((file, j) => {
        out.push({ file, alb, fan: FAN[j], scatter: SCATTER[i], i })
        i++
      })
    })
    return out
  }, [])

  return (
    <div className={styles.page}>
      <button className={styles.close} onClick={handleClose} aria-label="Close">
        <svg viewBox="0 0 24 24" width="17" height="17" aria-hidden>
          <path
            d="M6 6l12 12M18 6L6 18"
            stroke="currentColor"
            strokeWidth="2.6"
            strokeLinecap="round"
          />
        </svg>
      </button>

      {/* ── Header ─────────────────────────────────────────────── */}
      <header className={styles.header}>
        <div className={styles.kicker}>SMART ALBUMS</div>
        <h1 className={styles.title}>
          Your camera roll,
          <br />
          <span className={styles.titleAccent}>finally tidy.</span>
        </h1>
        <p className={styles.sub}>
          CloudVault sorts every photo into albums for you — and quietly bins
          the duplicates.
        </p>
      </header>

      {/* ── Stat pills ─────────────────────────────────────────── */}
      <div className={styles.stats}>
        <div className={styles.stat}>
          <span className={styles.statNum}>
            {photosTidied.toLocaleString("en-US")}
          </span>
          <span className={styles.statLbl}>sorted</span>
        </div>
        <div className={styles.statDiv} aria-hidden />
        <div className={styles.stat}>
          <span className={styles.statNum}>{albumsMade}</span>
          <span className={styles.statLbl}>albums</span>
        </div>
        <div className={styles.statDiv} aria-hidden />
        <div className={styles.stat}>
          <span className={`${styles.statNum} ${styles.statDupe}`}>
            {dupesGone}
          </span>
          <span className={styles.statLbl}>dupes binned</span>
        </div>
      </div>

      {/* ── Canvas: heap → album grid ──────────────────────────── */}
      <div className={styles.canvas}>
        {/* album trays (fade/scale in as it tidies) */}
        {ALBUMS.map((alb, idx) => {
          const appear = clamp01((progress - 0.12 - idx * 0.03) / 0.4)
          return (
            <div
              key={alb.key}
              className={styles.tray}
              style={{
                left: `${alb.cx}%`,
                top: `${alb.cy}%`,
                opacity: 0.18 + appear * 0.82,
                transform: `translate(-50%,-50%) scale(${lerp(0.84, 1, appear)})`,
              }}
            >
              <div className={styles.trayInner} />
              <div className={styles.trayLabel} style={{ opacity: appear }}>
                <span className={styles.trayEmoji}>{alb.emoji}</span>
                <span className={styles.trayName}>{alb.label}</span>
                <span className={styles.trayCount}>3</span>
              </div>
            </div>
          )
        })}

        {/* the photos */}
        {photos.map((p) => {
          const t = localP(p.i)
          const x = lerp(p.scatter.x, p.alb.cx, t)
          const y = lerp(p.scatter.y, p.alb.cy, t)
          const rot = lerp(p.scatter.rot, p.fan.rot, t)
          const scale = lerp(1, 0.6, t)
          const fanX = p.fan.dx * t
          const fanY = p.fan.dy * t
          return (
            <div
              key={p.file}
              className={styles.photo}
              style={{
                left: `${x}%`,
                top: `${y}%`,
                zIndex: 10 + Math.round(t * 3) + FAN.indexOf(p.fan),
                transform: `translate(-50%,-50%) translate(${fanX}px,${fanY}px) rotate(${rot}deg) scale(${scale})`,
              }}
            >
              <img src={src(p.file)} alt="" draggable={false} />
            </div>
          )
        })}

        {/* duplicate thumbnails that shrink + fade into the bin */}
        {DUPES.map((d, k) => {
          const gone = clamp01((progress - 0.3 - k * 0.06) / 0.28)
          const x = lerp(d.x, 50, gone)
          const y = lerp(d.y, 90, gone)
          return (
            <div
              key={`dupe-${k}`}
              className={styles.dupe}
              style={{
                left: `${x}%`,
                top: `${y}%`,
                opacity: (1 - gone) * 0.96,
                zIndex: 40,
                transform: `translate(-50%,-50%) rotate(${d.rot}deg) scale(${lerp(1, 0.2, gone)})`,
              }}
            >
              <img src={src(d.file)} alt="" draggable={false} />
              <span className={styles.dupeTag}>dupe</span>
            </div>
          )
        })}

        {/* bin chip */}
        <div
          className={styles.bin}
          style={{ opacity: progress > 0.28 ? 1 : 0.35 }}
        >
          🗑 {dupesGone} duplicates removed
        </div>
      </div>

      {/* ── Scrubber ───────────────────────────────────────────── */}
      <div className={styles.scrubWrap}>
        <div className={styles.scrubEnds}>
          <span>Messy</span>
          <span className={styles.scrubHint}>drag to tidy →</span>
          <span>Tidy ✨</span>
        </div>
        <div
          ref={trackRef}
          className={styles.track}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          <div className={styles.trackBase} />
          <div
            className={styles.trackFill}
            style={{ width: `${progress * 100}%` }}
          />
          <div className={styles.knob} style={{ left: `${progress * 100}%` }}>
            <div className={styles.knobDots} aria-hidden>
              <span />
              <span />
            </div>
          </div>
        </div>
        <div className={styles.statusLine}>{statusText}</div>
      </div>

      {/* ── Pricing ────────────────────────────────────────────── */}
      <div className={styles.pricing}>
        <button
          className={`${styles.yearTile} ${plan === "yearly" ? styles.yearOn : ""}`}
          onClick={() => setPlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          {savePct > 0 && (
            <span className={styles.saveBadge}>SAVE {savePct}%</span>
          )}
          <div className={styles.yearLeft}>
            <span className={styles.yearLabel}>Yearly</span>
            <span className={styles.yearSub}>
              {yearlyPerMonth}/mo · billed annually
            </span>
          </div>
          <div className={styles.yearRight}>
            <span className={styles.yearPrice}>{yearlyPrice}</span>
            <span className={styles.yearPer}>per year</span>
          </div>
        </button>

        <button
          className={`${styles.monthPill} ${plan === "monthly" ? styles.monthOn : ""}`}
          onClick={() => setPlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          Prefer monthly? <strong>{monthlyPrice}/mo</strong>
        </button>
      </div>

      <motion.button
        className={styles.cta}
        onClick={handlePurchase}
        whileTap={{ scale: 0.97 }}
      >
        <span className={styles.ctaTop}>
          {hasTrial && plan === "yearly"
            ? "Start free — then tidy it all"
            : "Tidy my library"}
        </span>
        <span className={styles.ctaSub}>
          {plan === "yearly"
            ? `${yearlyPrice}/yr · ${yearlyPerMonth}/mo`
            : `${monthlyPrice}/mo`}
        </span>
      </motion.button>

      <p className={styles.fine}>
        {plan === "yearly" && hasTrial
          ? "7-day free trial, then renews yearly. Cancel anytime."
          : "Auto-renews until cancelled. Cancel anytime."}{" "}
        Your photos stay yours, even if you cancel.
      </p>

      <div className={styles.legal}>
        <button onClick={() => native.postNativeEvent("already_purchased")}>
          Restore
        </button>
        <span aria-hidden>·</span>
        <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
        <span aria-hidden>·</span>
        <button onClick={() => native.postNativeEvent("privacy")}>
          Privacy
        </button>
      </div>
    </div>
  )
}

export default TidyPaywall
